import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import { getNavbarHeight, getViewportDimensions } from '../../../../models/Utils';

import { ReactComponent as CloseBtn } from '../../../../assets/images/graphics/btn-close.svg';

import { ADALandmarks, ADAARIARoles } from '../../../../models/ADA';

import LearnMoreLink from '../../../Common/LearnMoreLink/LearnMoreLink';

import './VideoSection.scss';

const VideoSection = React.forwardRef(({ data, sectionHeight, hideFromADA }, theRef) => {
  const menuOpened = useSelector(state => state.data.menuOpened);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [showingVideo, setShowingVideo] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState({ top: 0, width: 0, height: 0 });
  const [isPlaying, setIsPlaying] = useState(false);

  const handleResize = () => {
    const viewport = getViewportDimensions();
    const top = getNavbarHeight();
    setVideoDimensions({
      top,
      width: viewport.width,
      height: viewport.height - top
    });
    setContentHeight(Math.min(contentRef.current.getBoundingClientRect().width * 9 / 16, sectionHeight - 2 * 30));
  };

  const playVideo = () => {
    handleResize();
    setIsPlaying(true);
  };

  const handleButtonFocus = () => {
    const viewport = getViewportDimensions();
    const rect = contentRef.current.getBoundingClientRect();
    if (rect.top + rect.height > viewport.height) {
      try {
        contentRef.current.scrollIntoView({ block: 'center' });
      } catch (err) {
        contentRef.current.scrollIntoView(false);
      }
    }
  };

  useEffect(() => {
    handleResize();
  }, [sectionHeight]);

  useLayoutEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      className="credo-video-section"
      ref={theRef}
      role={ADAARIARoles.Group}
      aria-label={`Video section: ${data.Title}`}
      style={{
        visibility: hideFromADA ? 'hidden' : null
      }}
    >
      <div className="cpvi-grid-container">
        <div className="cpvi-grid-row">
          <div className="cpvi-grid-col w12">
            <div className="credo-vs-content-holder" style={{ height: `${sectionHeight}px`}}>
              <div className="credo-vs-content-wrapper" ref={contentRef} style={{ height: `${contentHeight}px` }}>
                <div className="credo-vs-content-inner">
                  <div className="credo-vs-aria-texts">
                    <div role={ADAARIARoles.Heading}>{data.Title}</div>
                    <div>{data.Subtitle}</div>
                  </div>
                  <div className="credo-vs-button-holder desktop"><button className="credo-vs-button" onClick={() => setShowingVideo(true)} onFocus={handleButtonFocus}><span>{data.PlayButtonAltText}</span></button></div>
                  <div className="credo-vs-title" aria-hidden={true} role={ADAARIARoles.Presentation}>{data.Title}</div>
                  <div className="credo-vs-subtitle" aria-hidden={true} role={ADAARIARoles.Presentation}>{data.Subtitle}</div>
                  <div className="credo-vs-button-holder mobile"><button className="credo-vs-button" onClick={() => setShowingVideo(true)} onFocus={handleButtonFocus}><span>{data.PlayButtonAltText}</span></button></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {
        showingVideo
        &&
        <div className="credo-vs-video-holder" style={{ top: `${videoDimensions.top}px` }}>
          <div className="credo-vs-video-player-wrapper">
            <ReactPlayer
              url={data.Video.URL}
              width={videoDimensions.width}
              height={videoDimensions.height}
              muted={false}
              loop={false}
              playing={isPlaying}
              playsinline={false}
              volume={1}
              controls={true}
              config={{
                youtube: data.Video.URL.indexOf("youtube.com") < 0 ? null : {
                  playerVars: {
                    // autoplay: 1,
                    showinfo: 1,
                    modestbranding: 1,
                  },
                  onUnstarted: () => playVideo()
                }
              }}
              onReady={ () => playVideo() }
            />
          </div>
          <CloseBtn
            className="credo-vs-video-close"
            tabIndex="0"
            role="button"
            aria-label={data.CloseButtonAltText}
            onKeyDown={() => setShowingVideo(false)}
            onClick={() => setShowingVideo(false)}
          />
        </div>
      }
    </div>
  );
});

export default VideoSection;